import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";

import { tellSentry } from "utils/Rave";
import { fetchFilenameConflicts, uploadCreative } from "reducers/Creatives";
import { fetchNameConflicts as fetchAssetNameConflicts } from "reducers/Assets";
import { RESOLUTIONS } from "hooks/useConflictResolver";
import { SUPPRESS_SUCCESS_GROWL, SUPPRESS_ALL_GROWL } from "utils/constants";
import { formatBytes, getFilenames, getFilenameExtension, MEGA } from "utils/common";
import ValidatingCreativesMessage from "widgets/messages/ValidatingCreativesMessage";
import UploadListMessage from "widgets/messages/UploadListMessage";
import useNotifications from "hooks/useNotifications";
import useFTMessageGrowl from "hooks/useFTMessageGrowl";
import UploadErrorModal from "widgets/modals/creatives/bulk/upload/UploadErrorModal";
import AssetConflictModal, { ASSET_CONFLICT_RESOLUTIONS } from "widgets/modals/creatives/bulk/upload/AssetConflictModal";
import { addNotification } from "reducers/Notifications";

export const CreativeUploadContext = React.createContext();

const CREATIVE_MAX_MEGABYTES = 500;
const CREATIVE_MAX_BYTES = CREATIVE_MAX_MEGABYTES * MEGA;
const UPLOAD_BATCH_TOPIC = "Upload Creative Batch";
const UPLOAD_CREATIVE_TASK = "Upload Creatives";

const ZIPPED_ASSET_CONFLICT_ERROR = 104;

export function zipFirstSort(creativeA, creativeB){
	let [isZipA, isZipB] = [creativeA, creativeB].map((creative) => {
		return /^zip$/i.test(getFilenameExtension(creative.filename));
	});

	// If neither or both are zips, leave the order alone.
	if((isZipA && isZipB) || (!isZipA && !isZipB)){
		return 0;
	}

	// Put whichever one is a zip first
	if(isZipA){
		return -1;
	} else {
		return 1;
	}
}

function generateFormData(files, resolutions){
	return files.reduce((prev, file) => {
		const conflictResolution = resolutions[file.name] || {};

		if(conflictResolution.resolution === RESOLUTIONS.RENAME && conflictResolution.newName){
			prev.append("file", file, conflictResolution.newName);
		} else {
			// This is assumed to be an overwrite
			prev.append("file", file);
		}
		return prev;
	}, new FormData());
}

function uploadBatchNotifications(detail){
	let { task, status, data } = detail?.message || {};
	if((task === UPLOAD_CREATIVE_TASK) && (status === "Success")){
		let numFiles = data?.files?.length;
		if(numFiles){ // This should always happen, but just in case ...
			let message = `Successfully uploaded ${numFiles} creative${numFiles > 1 ? "s" : ""}`;
			return {
				type: "success",
				messageHeader: "Upload Successful",
				message,
			};
		}
	}
}

function useCreativeUploadQueue(resolveAssetConflict){
	/*
		This manages dispatching uploads, and adding messages to the Notifications panel.

		`enqueueBatch` is used to add a group of files to the queue of batches.

		Batches are processed one at a time, and one file at a time.
			- Uploading many at once made it impossible to track individual errors, upload progress,
			  and would make whole batches fail if there was a problem with one file
			- We don't parallelize because it could produce too many requests at a time from one client
			  and overload the API
	*/
	const {
		addMessage,
		transformMessage,
	} = useNotifications();
	const dispatch = useDispatch();

	useFTMessageGrowl([uploadBatchNotifications]);

	const [batchFiles, setBatchFiles] = useState([]); // Files for the current batch
	const [batches, setBatches] = useState([]);

	const filesHead = batchFiles[0] || null;
	const batchHead = batches[0] || null;

	function enqueueBatch(batch){
		/*
			This is used to add a group of files to the queue of batches.
			`batch` is expected to be an object in the form:
			{
				libraryId,
				libraryName,
				files: [
					{
						libraryId,
						creativeId, // This is needed for hitting the correct overwrite endpoint
						formData,
						filename,
						size,
					}, ...
				]
			}
		*/
		let {
			libraryId,
			libraryName,
			files,
		} = batch;
		let numFiles = files.length;
		let batchId = Date.now();

		setBatches([...batches, {
			...batch,
			batchId,
		}]);

		addMessage(UPLOAD_BATCH_TOPIC, {
			id: batchId,
			jobId: batchId,
			origin: "HOPI",
			task: UPLOAD_CREATIVE_TASK,
			status: "In Progress",
			data: {
				libraryId,
				libraryName,
				header: `Uploading ${numFiles} creative${(numFiles > 1) ? "s" : ""}`,
				files,
			},
			component: UploadListMessage,
		});
	}

	function dequeueFile(){
		setBatchFiles(batchFiles.slice(1));
	}

	function addErrorMessage(filename, error){
		transformMessage(UPLOAD_BATCH_TOPIC, batchHead.batchId, (message) => {
			let messageData = message?.data || {};
			let messageErrors = messageData.errors || {};

			let updatedErrors = {
				...messageErrors,
				[filename]: error,
			};

			return {
				...message,
				data: {
					...messageData,
					errors: updatedErrors,
				},
			};
		});
	}

	useEffect(() => {
		// When the head batch changes, we have to repopulate batchFiles with its files.
		if(!filesHead && batchHead?.files){
			setBatchFiles(batchHead.files);
		}
	}, [batchHead]);

	useEffect(() => {
		if((batchFiles.length === 0) && batchHead){
			// We've emptied the batch's files, and can go to the next batch

			let { files } = batchHead;
			let numFiles = files.length;

			// Finalize the Notifications panel message about the batch
			transformMessage(UPLOAD_BATCH_TOPIC, batchHead.batchId, (message) => {
				let status = "Success";
				let header = `Uploaded ${numFiles} creative${(numFiles > 1) ? "s" : ""}`;

				let errors = message.data?.errors || {};
				let numErrors = Object.keys(errors).length;

				if(numErrors === numFiles){
					status = "Failed";
					header = `${numErrors} creative upload${(numFiles > 1) ? "s" : ""} failed`;
				} else if(numErrors > 0){
					status = "warn";
					header = `${numErrors} of ${numFiles} creative upload${(numFiles > 1) ? "s" : ""} failed`;
				}

				return {
					...message,
					status,
					data: {
						...(message?.data || {}),
						header,
					},
				};
			});

			setBatches(batches.slice(1));
		}
	}, [batchFiles]);

	useEffect(async () => {
		if(!filesHead){
			return;
		}

		let {
			libraryId,
			creativeId,
			formData,
			filename,
			size,
		} = filesHead;

		let isZip = /\.zip$/i.test(filename);

		let maxSize = CREATIVE_MAX_BYTES;
		if(size > maxSize){
			/*
				These files aren't uploaded, but we want them added to the list in the Notifications panel
				so we can display the associated error, which is why they aren't filtered out earlier with
				the files that the user chose to skip.
			*/
			addErrorMessage(filename, `File size exceeds ${formatBytes(maxSize)}`);
			dequeueFile();
			return;
		}

		function updateProgress(progressEvent){
			let { batchId } = batchHead;
			let { loaded, total } = progressEvent;

			function updateMessageProgress(message){
				let messageData = message?.data || {};
				let { progress = {} } = messageData;
				return {
					...message,
					data: {
						...messageData,
						progress: {
							...progress,
							[filename]: {
								loaded,
								total,
							},
						},
					},
				};
			}
			transformMessage(UPLOAD_BATCH_TOPIC, batchId, updateMessageProgress);
		}

		function processError(result){
			let { description, errorMessage } = result?.payload?.errorObj || {};
			let error = description || errorMessage || result.payload?.error?.message || "Upload error"; /*
				Generally, the `errorObj.description` has the message that we want, but when we get a 504 gateway timeout,
				`errorObj` is just an empty string, so we fall back to `error.message`, which is better than nothing.
			*/
			addErrorMessage(filename, error);
		}

		let result = await dispatch(uploadCreative(libraryId, formData, {
			creativeId,
			onUploadProgress: updateProgress,
			meta: isZip ? SUPPRESS_ALL_GROWL : SUPPRESS_SUCCESS_GROWL,
		}));

		if(!result.success){
			let errorObj = result?.payload?.errorObj;
			let errorCode = errorObj?.error;
			if(errorCode === ZIPPED_ASSET_CONFLICT_ERROR){
				/*
					The zip we uploaded had images in its manifest that conflict with existing assets in the library.
					These are in a `conflicts` object, with arrays of filenames keyed by asset type.

					It should only be possible to get images here, so we ignore anything else.
				*/
				let files = errorObj?.conflicts?.images || [];
				if(files.length){
					let assetConflicts = await dispatch(fetchAssetNameConflicts(libraryId, "images", files));

					// Kick control back to the upload context to get the user's chosen resolution
					let assetConflictResolution = await resolveAssetConflict(filename, assetConflicts);

					if(assetConflictResolution === ASSET_CONFLICT_RESOLUTIONS.SKIP){
						addErrorMessage(filename, "Upload skipped due to asset conflicts");
					} else {
						// Retry the upload with the user specified Asset conflict resolution flag
						let result = await dispatch(uploadCreative(libraryId, formData, {
							creativeId,
							onUploadProgress: updateProgress,
							meta: SUPPRESS_SUCCESS_GROWL,
							assetConflictResolution,
						}));

						if(!result.success){
							processError(result);
						}
					}
				} else {
					// We got a malformed payload with no file list, so just try to handle it generically.
					tellSentry(`Received no conflicted image list for zipped Creative ${filename} in library ${libraryId}`);
					processError(result);
				}
			} else {
				// This is a more generic error that we can just add a notification for
				const { name, description, errorMessage } = result?.payload?.errorObj || {};
				const error = description || errorMessage || result.payload?.error?.message || "Upload error";

				if (isZip) {
					dispatch(addNotification({
						type: "error",
						id: Math.random(),
						messageHeader: name || "Error",
						message: error,
					}));
				}
				processError(result);
			}
		}

		dequeueFile();
	}, [filesHead]);

	return [enqueueBatch];
}

export function CreativeUploadController({ children }){
	/*
		This component will manage the logic for processing a bulk creative upload, which will involve
		producing FormData to send to the creative import or overwrite endpoint.

		Components that subscribe to the `CreativeUploadContext` can call the `stageUpload` function, provided
		in the context's `value`, providing the library ID, and a FileList object, provided
		from a file input.  Calling `stageUpload` will kick off the upload process:

			- It will initially check for conflicting creative names that exist in the library already,
			and then prompt the user for how to resolve the conflicts, if they exist.
				- If they don't exist, we'll simply upload the files without any intervention from the user.
			- If users upload a .zip, they will have their manifest files checked for assets with names
			that already exist in the library, and will by prompted for how to resolve those conflicts as well

		Users will have the option to skip, overwrite or rename any conflicted files.
	*/
	const dispatch = useDispatch();

	const [libraryId, setLibraryId] = useState(null);
	const [libraryName, setLibraryName] = useState("Library");
	const [fileList, setFileList] = useState(null);
	const [conflicts, setConflicts] = useState([]);
	const [assetConflict, setAssetConflict] = useState(null);
	const [isUploading, setIsUploading] = useState(false);
	const [isValidating, setIsValidating] = useState(false);

	const {
		addMessage,
		transformMessage,
		readMessage,
	} = useNotifications();

	function resetState(){
		setLibraryId(null);
		setLibraryName("Library");
		setFileList(null);
		setConflicts([]);
		setIsValidating(false);
		setIsUploading(false);
	}

	const filenames = useMemo(() => getFilenames(fileList), [fileList]);
	const namesInUse = useMemo(() => {
		/*
			We need to pass the names that aren't conflicted to the UploadErrorModal so that
			they're not allowed to be used when renaming the conflicted files.
		*/
		let nameSet = new Set(filenames);

		conflicts.forEach((conflict) => {
			let { filename } = conflict;
			nameSet.delete(filename);
		});

		return [...nameSet];
	}, [filenames, conflicts]);


	function stageUpload(targetLibraryId, targetLibraryName, newFileList){
		setLibraryId(targetLibraryId);
		setLibraryName(targetLibraryName);
		setFileList(newFileList);
		setConflicts([]);
		setIsUploading(true);
	}

	function resolveAssetConflict(creativeName, conflicts){
		return new Promise((resolve) => {
			setAssetConflict({
				creativeName,
				conflicts,
				commitResolution: resolve,
			});
		});
	}

	let [enqueueBatch] = useCreativeUploadQueue(resolveAssetConflict);

	useEffect(async () => {
		if(!libraryId || !fileList){
			return;
		}

		let message = {
			origin: "HOPI",
			task: "Validating Upload",
			status: "In Progress",
			data: {
				libraryName,
				numFiles: fileList.length,
			},
			component: ValidatingCreativesMessage,
		};
		let validatingMessageId = addMessage("Validating Upload", message);
		setIsValidating(true);

		try{
			let newConflicts = await dispatch(fetchFilenameConflicts(libraryId, filenames));

			readMessage(validatingMessageId);
			setIsValidating(false);

			if(newConflicts.length){
				setConflicts(newConflicts);
			} else {
				// There are no conflicts, safe to upload
				startUpload();
			}
		} catch(validationError){
			// There was a problem with the conflicts API check
			transformMessage("Validating Upload", validatingMessageId, (message) => {
				return {
					...message,
					status: "Failed",
				};
			});
			resetState();
		}
	}, [libraryId, fileList]);


	function startUpload({
		resolutions = {},
	} = {}){
		let fileArray = [...(fileList || [])]; // FileList doesn't have normal array operations, so we convert it

		// Filter out files that the user chose to skip
		let filesToUpload = fileArray.filter(file => resolutions[file.name]?.resolution !== RESOLUTIONS.SKIP);

		if(filesToUpload.length === 0){
			resetState();
			return;
		}

		let batchFiles = filesToUpload.map((file) => {
			let conflict = conflicts.find(item => item.filename === file.name);
			let formData = generateFormData([file], resolutions);
			let { resolution, newName } = resolutions[file.name] || {};
			let filename = ((resolution === RESOLUTIONS.RENAME) && newName) ? newName : file.name;

			return {
				libraryId,
				creativeId: (resolution !== RESOLUTIONS.RENAME) ? conflict?.id : null, /*
					If creativeId is provided to `uploadCreative`, it will attempt to overwrite that creative.
					If the resolution is to rename, then we don't need to overwrite, so we can set it to `null`
				*/
				formData,
				filename,
				size: file.size,
			};
		});

		batchFiles.sort(zipFirstSort);

		enqueueBatch({
			libraryId,
			libraryName,
			files: batchFiles,
		});

		resetState();
	}

	const modalProps = {
		libraryId,
		conflicts,
		namesInUse,

		onCancel: resetState,
		onCommit: startUpload,
	};

	const value = {
		stageUpload,
		isValidating, // When validating, we should disable any upload creatives buttons
		isUploading,
	};

	return (
		<>
			<CreativeUploadContext.Provider value={value}>
				{children}
			</CreativeUploadContext.Provider>
			{ conflicts?.length ? <UploadErrorModal {...modalProps} /> : null }
			{ assetConflict ? <AssetConflictModal {...assetConflict} onClose={() => { setAssetConflict(null); }} /> : null }
		</>
	);
}
